import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getArticles, getCategories } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import { Skeleton } from "../components/ui/skeleton";
import { Ship, Building, UtensilsCrossed, ArrowLeft } from "lucide-react";

const categoryIcons = {
  croisieres: Ship,
  hotels: Building,
  restaurants: UtensilsCrossed,
};

const categoryDescriptions = {
  croisieres: "Embarquez pour des aventures inoubliables à travers les océans du monde",
  hotels: "Découvrez les plus beaux établissements pour des séjours d'exception",
  restaurants: "Savourez les meilleures expériences gastronomiques",
};

const heroImages = {
  croisieres: "https://images.unsplash.com/photo-1761514745080-7cab3a9ad041?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjcnVpc2UlMjBzaGlwJTIwb2NlYW4lMjBzdW5zZXR8ZW58MHx8fHwxNzc0MzQ1OTgzfDA&ixlib=rb-4.1.0&q=85",
  hotels: "https://images.unsplash.com/photo-1759264244746-140bbbc54e1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3IlMjBiZWRyb29tfGVufDB8fHx8MTc3NDM0NTk4NXww&ixlib=rb-4.1.0&q=85",
  restaurants: "https://images.pexels.com/photos/32399760/pexels-photo-32399760.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadData = async () => {
    setLoading(true);
    try {
      const categoriesRes = await getCategories();
      const cat = Array.isArray(categoriesRes.data)
        ? categoriesRes.data.find(c => c.slug === slug)
        : undefined;
      setCategory(cat);

      if (cat) {
        const articlesRes = await getArticles({ category: cat.id, status: "published" });
        setArticles(articlesRes.data);
      }
    } catch (error) {
      console.error("Error loading category:", error);
    } finally {
      setLoading(false);
    }
  };

  const Icon = categoryIcons[slug] || Ship;
  const heroImage = heroImages[slug] || heroImages.croisieres;
  const description = categoryDescriptions[slug] || "Explorez nos articles";

  return (
    <div className="min-h-screen bg-secondary" data-testid="category-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh]">
        <img
          src={heroImage}
          alt={category?.name || slug}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
        
        {/* Back Button */}
        <Link
          to="/"
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="h-5 w-5" />
          Accueil
        </Link>

        {/* Category Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20">
          <div className="max-w-4xl flex items-end gap-6">
            <div className="hidden md:flex w-20 h-20 bg-accent rounded-sm items-center justify-center">
              <Icon className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                {category?.name || slug}
              </h1>
              <p className="text-white/80 text-lg max-w-xl">
                {category?.description || description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex items-center justify-between mb-12">
            <p className="text-muted-foreground">
              {loading ? "Chargement..." : `${articles.length} article${articles.length > 1 ? 's' : ''}`}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(6).fill(0).map((_, i) => (
                <div key={i}>
                  <Skeleton className="aspect-[4/3] mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {articles.map((article, index) => (
                <div 
                  key={article.id} 
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Icon className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-semibold text-primary mb-2">
                Aucun article pour le moment
              </h3>
              <p className="text-muted-foreground">
                Revenez bientôt pour découvrir nos dernières publications.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
